import { useEffect, useState } from "react";
import { Card, CardHeader } from "../../design-system/Card";
import { Badge } from "../../design-system/Badge";
import { Button } from "../../design-system/Button";
import { StatCard } from "../../design-system/StatCard";
import { Icon, type IconName } from "../../design-system/Icon";
import { LoadingState, ErrorState } from "../../components/States";
import { useIpc, useIpcQuery } from "../../ipc/IpcProvider";
import { ProductPicker } from "../../components/ProductPicker";
import { useConfirm } from "../../components/ConfirmDialog";
import { firstProblem, minLen, isPositiveAmount, isPositiveInt } from "../../components/validators";
import type { EngineOverviewDto, PurchaseAction, PurchaseSupplierDto, ProductCatalogItem, PeriodRange } from "../../ipc/types";
import { useDateRange } from "../../components/DateRangeContext";
import { formatRange } from "../../lib/date-range";
import { CopyDataButton } from "../../components/CopyDataButton";
import "../dashboard/dashboard.css";
import "../product-development/product-development.css";

/** Acciones disponibles según el estado mostrado en la fila. */
function actionsFor(statusLabel: string): PurchaseAction[] {
  if (statusLabel === "Borrador") return ["approve", "cancel"];
  if (statusLabel === "Aprobada" || statusLabel === "Recepción parcial") return ["receive", "cancel"];
  return [];
}
const ACTION_LABEL: Record<PurchaseAction, string> = { approve: "Aprobar", cancel: "Cancelar", receive: "Recibir" };

function NewPurchaseForm({ suppliers, catalog, onCreate, onCancel, busy }: {
  suppliers: readonly PurchaseSupplierDto[];
  catalog: readonly ProductCatalogItem[];
  onCreate: (v: { supplierCode: string; sku: string; description: string; unitCostMinor: number; quantity: number }) => void;
  onCancel: () => void; busy: boolean;
}): JSX.Element {
  const [supplierCode, setSupplierCode] = useState(suppliers[0]?.code ?? "");
  const [sku, setSku] = useState("");
  const [name, setName] = useState("");
  const [cost, setCost] = useState("");
  const [qty, setQty] = useState("1");
  const costNum = Number(cost);
  const qtyNum = Number(qty);
  const hint = firstProblem([
    { ok: supplierCode.length > 0, msg: "Elige un proveedor" },
    { ok: minLen(sku, 2), msg: "Elige un producto del catálogo" },
    { ok: isPositiveAmount(cost), msg: "El costo debe ser un número mayor que 0" },
    { ok: isPositiveInt(qty), msg: "La cantidad debe ser un entero mayor que 0" },
  ]);
  const valid = hint === null;
  return (
    <Card style={{ marginBottom: "var(--sp-5)" }}>
      <CardHeader title="Nueva orden de compra" subtitle="Elige un producto de tu catálogo; al recibirla suma stock a Inventario" />
      <div className="card__body">
        <div className="pd-form">
          <div className="pd-field"><span className="pd-field__label">Proveedor</span>
            <select className="pd-input" value={supplierCode} aria-label="Proveedor" onChange={(e) => setSupplierCode(e.target.value)}>
              {suppliers.map((s) => <option key={s.code} value={s.code}>{s.name} ({s.code})</option>)}
            </select></div>
          <div className="pd-field"><span className="pd-field__label">Costo unitario (CLP)</span>
            <input className="pd-input" value={cost} type="number" min="0" placeholder="Ej: 22000" aria-label="Costo unitario" onChange={(e) => setCost(e.target.value)} /></div>
          <div className="pd-field"><span className="pd-field__label">Cantidad</span>
            <input className="pd-input" value={qty} type="number" min="1" aria-label="Cantidad" onChange={(e) => setQty(e.target.value)} /></div>
          <div className="pd-field pd-form__full"><span className="pd-field__label">Producto (SKU)</span>
            <ProductPicker catalog={catalog} value={sku} onChange={setSku} ariaLabel="Producto" onPick={(item) => setName(item.name)} /></div>
        </div>
        <div className="pd-actions">
          {hint ? <span className="faint" style={{ fontSize: 12.5, marginRight: "auto" }}>{hint}</span> : null}
          <Button variant="ghost" size="sm" onClick={onCancel}>Cancelar</Button>
          <Button size="sm" icon="check" disabled={!valid || busy} onClick={() => onCreate({ supplierCode, sku: sku.trim(), description: name || sku.trim(), unitCostMinor: Math.round(costNum), quantity: Math.round(qtyNum) })}>
            {busy ? "Creando…" : "Crear orden"}
          </Button>
        </div>
      </div>
    </Card>
  );
}

export function PurchaseScreen(): JSX.Element {
  const client = useIpc();
  const { range, previous, compare } = useDateRange();
  const periodRange: PeriodRange = {
    fromEpoch: range.fromEpoch, toEpoch: range.toEpoch,
    prevFromEpoch: compare ? previous.fromEpoch : 0,
    prevToEpoch: compare ? previous.toEpoch : 0,
  };
  const { state, reload } = useIpcQuery<EngineOverviewDto>(
    (c) => c.getEngineOverview("purchase", periodRange),
    [range.fromEpoch, range.toEpoch, previous.fromEpoch, previous.toEpoch, compare],
  );
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [suppliers, setSuppliers] = useState<readonly PurchaseSupplierDto[]>([]);
  const [catalog, setCatalog] = useState<readonly ProductCatalogItem[]>([]);
  const { ask, node } = useConfirm();

  useEffect(() => {
    let active = true;
    client.listPurchaseSuppliers().then((res) => { if (active && res.ok) setSuppliers(res.value.items); });
    client.getProductCatalog().then((res) => { if (active && res.ok) setCatalog(res.value.items); });
    return () => { active = false; };
  }, [client]);

  if (state.status === "loading") return <LoadingState />;
  if (state.status === "error") return <ErrorState message={state.error.message} onRetry={reload} />;

  const data = state.data;

  const create = async (v: { supplierCode: string; sku: string; description: string; unitCostMinor: number; quantity: number }): Promise<void> => {
    setBusy(true); setMsg(null);
    const res = await client.createPurchase(v);
    setBusy(false);
    if (res.ok && res.value.ok) { setShowForm(false); setMsg(`Orden creada para ${v.supplierCode}.`); reload(); }
    else setMsg(res.ok ? res.value.message ?? "No se pudo crear" : res.error.message);
  };
  const act = async (id: string, action: PurchaseAction): Promise<void> => {
    if (action === "cancel") {
      const ok = await ask({ title: "Cancelar orden de compra", message: "La OC quedará cancelada. Esta acción no se puede deshacer. ¿Continuar?", confirmLabel: "Sí, cancelar", danger: true });
      if (!ok) return;
    }
    setBusy(true); setMsg(null);
    const res = await client.setPurchaseStatus(id, action);
    setBusy(false);
    if (res.ok && res.value.ok) reload();
    else setMsg(res.ok ? res.value.message ?? "Acción no permitida" : res.error.message);
  };

  return (
    <div>
      {node}
      <div className="pagehead">
        <div className="pagehead__row">
          <div className="row" style={{ gap: "var(--sp-4)" }}>
            <div className="sidebar__logo" style={{ width: 44, height: 44 }}><Icon name="purchase" size={22} /></div>
            <div>
              <h1 className="pagehead__title">Compras</h1>
              <p className="pagehead__sub">Órdenes de compra a proveedores. Crea, aprueba y cancela; se guardan en tu equipo.</p>
              {data.period ? (
                <div className="row" style={{ gap: 8, marginTop: 4 }}>
                  <Badge tone="brand" dot>{formatRange({ fromEpoch: data.period.fromEpoch, toEpoch: data.period.toEpoch })}</Badge>
                  {compare ? <span className="faint" style={{ fontSize: 12 }}>vs período anterior</span> : null}
                </div>
              ) : null}
            </div>
          </div>
          <div className="row">
            <CopyDataButton data={{ title: data.title, columns: data.columns, rows: data.rows, metrics: data.metrics, ...(data.period ? { periodLabel: formatRange(data.period) } : {}) }} />
            <Button size="sm" icon="purchase" disabled={suppliers.length === 0} onClick={() => setShowForm((s) => !s)}>Nueva orden</Button>
          </div>
        </div>
      </div>

      <div className="kpis" style={{ marginTop: 0 }}>
        {data.metrics.map((m) => (
          <StatCard key={m.label} label={m.label} value={m.value} icon={m.icon as IconName} accent={m.accent}
            {...(typeof m.deltaPct === "number" ? { deltaPct: m.deltaPct } : {})} />
        ))}
      </div>

      {showForm ? <div style={{ marginTop: "var(--sp-5)" }}><NewPurchaseForm suppliers={suppliers} catalog={catalog} onCreate={create} onCancel={() => setShowForm(false)} busy={busy} /></div> : null}

      <Card style={{ marginTop: "var(--sp-5)" }}>
        <CardHeader title="Órdenes de compra" subtitle={`${data.rows.length} órdenes`} action={msg ? <span className="muted" style={{ fontSize: 12.5 }}>{msg}</span> : null} />
        <div className="card__body" style={{ padding: 0 }}>
          {data.rows.length === 0 ? (
            <div className="statecard"><Icon name="purchase" size={26} style={{ color: "var(--text-faint)" }} /><div className="strong">Aún no tienes órdenes de compra</div><div className="faint" style={{ fontSize: 13 }}>Crea la primera con “Nueva orden”.</div></div>
          ) : (
            <table className="table">
              <thead><tr><th>Orden</th><th>Proveedor</th><th>Estado</th><th>Total</th><th>Acciones</th></tr></thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.id}>
                    <td><div className="strong">{r.primary}</div></td>
                    <td className="muted">{r.secondary}</td>
                    <td><Badge tone={r.status.tone} dot>{r.status.label}</Badge></td>
                    <td className="strong">{r.amount}</td>
                    <td>
                      <div className="row" style={{ gap: 6 }}>
                        {actionsFor(r.status.label).map((a) => (
                          <Button key={a} variant={a === "approve" ? "secondary" : "ghost"} size="sm" disabled={busy} onClick={() => act(r.id, a)}>{ACTION_LABEL[a]}</Button>
                        ))}
                        {actionsFor(r.status.label).length === 0 ? <span className="faint" style={{ fontSize: 12 }}>—</span> : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>
    </div>
  );
}
